// Super Admin custom-domain lifecycle presentation rules.
//
// The backend is authoritative and re-validates every action. These rules only
// decide which controls the UI offers for the *persisted* record, and they are
// deliberately stricter than the backend: idempotent repeats (re-verify,
// re-approve, disabling routing that is already off) are not offered so the
// UI walks the lifecycle strictly forward: NOT_CONFIGURED → PROPOSED →
// VERIFIED → APPROVED. Routing is a separate, explicit activation decision.

export const DOMAIN_STATUS = Object.freeze({
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  PROPOSED: 'PROPOSED',
  VERIFIED: 'VERIFIED',
  APPROVED: 'APPROVED',
});

export const DOMAIN_STATUS_LABELS = Object.freeze({
  NOT_CONFIGURED: 'Not configured',
  PROPOSED: 'Proposed',
  VERIFIED: 'Verified',
  APPROVED: 'Approved',
});

export const DOMAIN_ACTION_ORDER = Object.freeze([
  'verify',
  'approve',
  'enable_routing',
  'disable_routing',
  'reset_to_proposed',
]);

export const DOMAIN_VERIFY_NOTICE =
  'AlgoSys does not check DNS or HTTPS automatically. Before marking this domain verified, '
  + 'independently confirm that the Operator controls its DNS records and that it serves a valid '
  + 'HTTPS certificate.';

export const DOMAIN_ROUTING_NOTICE =
  'Enabling routing only sets this Operator\u2019s routing flag. The custom portal works publicly '
  + 'only when the server Nginx configuration, HTTPS certificate, trusted-host configuration and '
  + 'the global domain-routing feature flag are also ready.';

export const DOMAIN_ACTIONS = Object.freeze({
  verify: {
    label: 'Mark Verified',
    title: 'Mark domain verified',
    body: 'This records that you have manually confirmed DNS ownership and HTTPS for this domain. '
      + 'It does not approve the domain or enable routing.',
    acknowledgement: 'I have independently confirmed DNS ownership and a valid HTTPS certificate for this domain.',
    success: 'Domain marked verified',
    tone: 'primary',
  },
  approve: {
    label: 'Approve Domain',
    title: 'Approve domain',
    body: 'This approves the verified domain for this Operator. Routing stays disabled until you enable it separately.',
    acknowledgement: null,
    success: 'Domain approved',
    tone: 'primary',
  },
  enable_routing: {
    label: 'Enable Routing',
    title: 'Enable domain routing',
    body: DOMAIN_ROUTING_NOTICE,
    acknowledgement: 'I confirm the server Nginx, HTTPS, trusted-host and global routing configuration are ready, '
      + 'or I understand the portal will not work publicly until they are.',
    success: 'Domain routing enabled',
    tone: 'danger',
  },
  disable_routing: {
    label: 'Disable Routing',
    title: 'Disable domain routing',
    body: 'Customers will no longer be routed to this Operator portal through the custom domain. '
      + 'The domain stays approved.',
    acknowledgement: null,
    success: 'Domain routing disabled',
    tone: 'danger',
  },
  reset_to_proposed: {
    label: 'Reset to Proposed',
    title: 'Reset domain to proposed',
    body: 'This clears verification and approval, disables routing and returns the domain to the proposed state. '
      + 'It must be verified and approved again before routing can be enabled.',
    acknowledgement: null,
    success: 'Domain reset to proposed',
    tone: 'danger',
  },
});

// Returns the set of actions valid for the persisted record. Unknown statuses,
// a missing domain or a record for a different Operator offer nothing.
export function availableDomainActions(branding, expectedOperatorId) {
  const allowed = new Set();
  if (!branding || typeof branding !== 'object') return allowed;
  if (!Number.isInteger(expectedOperatorId) || branding.operator_id !== expectedOperatorId) return allowed;
  if (typeof branding.proposed_domain !== 'string' || !branding.proposed_domain) return allowed;
  const status = branding.domain_status;
  const routing = branding.domain_routing_enabled === true;

  if (status === DOMAIN_STATUS.PROPOSED) allowed.add('verify');
  if (status === DOMAIN_STATUS.VERIFIED) allowed.add('approve');
  if (status === DOMAIN_STATUS.APPROVED && !routing) allowed.add('enable_routing');
  if (routing) allowed.add('disable_routing');
  if (status === DOMAIN_STATUS.VERIFIED || status === DOMAIN_STATUS.APPROVED) allowed.add('reset_to_proposed');
  return allowed;
}

// Fixed backend conflict details that are safe to show verbatim. Anything else
// falls back to a generic message so no unexpected server text is rendered.
const KNOWN_CONFLICTS = Object.freeze([
  'A proposed domain is required',
  'The main AlgoSys domain cannot be assigned to an Operator',
  'Approved domain must be reset before verification',
  'Domain must be verified before approval',
  'Domain must be approved before routing can be enabled',
]);

export function domainLifecycleErrorMessage(err) {
  const status = err?.status;
  if (status === 403) return 'Only a Super Admin can manage Operator custom domains.';
  if (status === 404) return 'This Operator was not found or is not active.';
  if (status === 409) {
    const detail = typeof err?.data?.detail === 'string' ? err.data.detail : '';
    const known = KNOWN_CONFLICTS.find((msg) => msg === detail);
    return known
      ? `${known}.`
      : 'The domain state changed or the action is not allowed. Reload and review the current status.';
  }
  if (status === 422 || status === 400) return 'The request was rejected as invalid.';
  return 'The domain action could not be completed. Please try again.';
}

export function domainBrandingLoadErrorMessage(err) {
  const status = err?.status;
  if (status === 403) return 'Only a Super Admin can view Operator custom domains.';
  if (status === 404) return 'Custom domain settings are unavailable because this Operator is not active.';
  return 'Failed to load custom domain settings.';
}
